import {
    mapReferences,
    Plan,
    prepare,
    readArchive,
    readFiles,
    resolve,
    writeArchive,
} from '@photostat/experience-package';
import { SchemaValidator } from '../validation/schemaValidator';
import { API_URL } from '../../services/api';
export const hashBytes = async (bytes: Uint8Array) =>
    Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
export const errorMessage = (error: any) =>
    error.response?.data?.message ||
    error.message ||
    'Package operation failed';
export async function readSelection(selection: FileList | File[]) {
    const list = Array.from(selection);
    if (!list.length) throw Error('Select an experience package');
    if (
        list.length > 2000 ||
        list.reduce((n, f) => n + f.size, 0) > 500 * 1024 * 1024
    )
        throw Error('Package exceeds 500 MiB or 2,000 files');
    if (list.length === 1 && /\.zip$/i.test(list[0].name))
        return readArchive(new Uint8Array(await list[0].arrayBuffer()));
    const files: Record<string, Uint8Array> = {};
    for (const file of list) {
        const path = file.webkitRelativePath || file.name;
        if (files[path])
            throw Error(
                `Duplicate selected filename: ${path}. Select the containing folder instead.`,
            );
        files[path] = new Uint8Array(await file.arrayBuffer());
    }
    return readFiles(files);
}
export function validatePrepared(plan: Plan) {
    const urls = Object.fromEntries(
        plan.assets.map((a) => [
            a.sha256,
            `https://package.invalid/${a.sha256}`,
        ]),
    );
    const result = SchemaValidator.getInstance().validateDefinition(
        resolve(plan.definition, urls),
    );
    if (!result.isValid)
        throw Error(
            result.errors
                .slice(0, 6)
                .map((e) => `${e.path}: ${e.message}`)
                .join('\n'),
        );
}
export async function createTransfer(
    api: any,
    route: string,
    plan: Plan,
    baseRevision: number,
) {
    validatePrepared(plan);
    const body = {
        definition: plan.definition,
        assets: plan.assets,
        baseRevision,
    };
    const key =
        'photostat-package:' +
        (await hashBytes(
            new TextEncoder().encode(API_URL + route + JSON.stringify(body)),
        ));
    let existing: string | null = null;
    try {
        existing = sessionStorage.getItem(key);
    } catch {}
    if (existing) {
        try {
            const saved = (await api.get(`${route}/transfers/${existing}`))
                .data;
            if (saved.receipt) {
                const current = (await api.get(`${route}/package-manifest`))
                    .data;
                if (current.baseRevision !== saved.receipt.version)
                    throw Error(
                        'Experience changed since that import. Refresh and review before saving.',
                    );
            }
            return saved;
        } catch (error: any) {
            if (error.response?.status !== 404) throw error;
        }
    }
    const result = (await api.post(`${route}/transfers/plan`, body)).data;
    try {
        sessionStorage.setItem(key, result.id);
    } catch {}
    return result;
}
export async function executeTransfer(
    api: any,
    route: string,
    plan: any,
    blobs: Record<string, Uint8Array>,
    progress: (text: string) => void,
): Promise<any> {
    const current = (await api.get(`${route}/transfers/${plan.id}`)).data;
    if (current.receipt) return current.receipt;
    const pending = current.assets.filter((a: any) => a.status === 'upload');
    let index = 0,
        done = 0,
        failure: any;
    async function worker() {
        while (index < pending.length && !failure) {
            const asset = pending[index++];
            try {
                const bytes = blobs[asset.sha256];
                if (!bytes)
                    throw Error(`Reselect the package to upload ${asset.name}`);
                const form = new FormData();
                form.append(
                    'asset',
                    new Blob([bytes], { type: asset.mimeType }),
                    asset.name,
                );
                progress(
                    `Uploading ${done + 1} of ${pending.length}: ${asset.name}`,
                );
                for (let attempt = 0; ; attempt++) {
                    try {
                        await api.post(
                            `${route}/transfers/${plan.id}/assets/${asset.sha256}`,
                            form,
                            {
                                headers: {
                                    'Content-Type': 'multipart/form-data',
                                },
                            },
                        );
                        break;
                    } catch (error: any) {
                        const status = error.response?.status;
                        if (
                            attempt >= 2 ||
                            (status && status !== 429 && status < 500)
                        )
                            throw error;
                        progress(`Retrying ${asset.name}…`);
                        await new Promise((resolve) =>
                            setTimeout(resolve, 500 * 2 ** attempt),
                        );
                    }
                }
                done++;
            } catch (error) {
                failure = error;
            }
        }
    }
    await Promise.all(
        Array.from({ length: Math.min(3, pending.length) }, worker),
    );
    if (failure) throw failure;
    progress('Saving the experience…');
    return (await api.post(`${route}/transfers/${plan.id}/commit`, {})).data;
}
export async function pushDefinition(
    api: any,
    route: string,
    definition: any,
    revision: number,
    progress: (text: string) => void,
) {
    const plan = await prepare(definition, {}, hashBytes);
    const transfer = await createTransfer(api, route, plan, revision);
    return executeTransfer(api, route, transfer, plan.blobs, progress);
}
export async function exportPackage(
    api: any,
    route: string,
    orgId: number,
    activationId: number,
    includeFiles: boolean,
    progress: (text: string) => void,
    draft?: { definition: any; baseRevision: number },
) {
    let snapshot: any;
    try {
        snapshot = (await api.get(`${route}/package-manifest`)).data;
    } catch (error: any) {
        if (!draft || error.response?.status !== 404) throw error;
        snapshot = { assets: [], baseRevision: 0 };
    }
    const definition = draft?.definition ?? snapshot.definition;
    const cloud: Record<string, any> = {},
        files: Record<string, Uint8Array> = {},
        replacements: Record<string, string> = {};
    const urls = Array.from(
        new Set(
            mapReferences(definition, (s) => s)
                .references.map((r) => r.source)
                .filter((s) => s.startsWith('https://')),
        ),
    );
    if (
        includeFiles &&
        draft &&
        urls.some((url) => !snapshot.assets.some((a: any) => a.url === url))
    ) {
        const library = (await api.get(`/${orgId}/assets`)).data;
        snapshot.assets = [...snapshot.assets, ...library];
    }
    if (includeFiles)
        for (let i = 0; i < urls.length; i++) {
            const url = urls[i],
                asset = snapshot.assets.find((a: any) => a.url === url);
            if (!asset)
                throw Error(
                    `This experience references a file outside the asset library: ${url}. Choose links only, or import that file into the library first.`,
                );
            progress(`Downloading ${i + 1} of ${urls.length}: ${asset.name}`);
            const response = await api.get(
                `${route}/package-assets/${asset.id}`,
                { responseType: 'arraybuffer' },
            );
            const bytes = new Uint8Array(response.data),
                hash = await hashBytes(bytes),
                path = `assets/${hash}-${asset.name}`;
            files[path] = bytes;
            replacements[url] = path;
            cloud[hash] = {
                assetId: asset.id,
                url,
                sha256: hash,
                environment: API_URL,
                orgId,
            };
        }
    const materialized = mapReferences(
        definition,
        (s) => replacements[s] || s,
    ).definition;
    const plan = await prepare(materialized, files, hashBytes);
    return writeArchive(
        plan,
        {
            environment: API_URL,
            orgId,
            activationId,
            baseRevision: draft?.baseRevision ?? snapshot.baseRevision,
            draft: !!draft,
        },
        cloud,
    );
}
export function downloadBytes(bytes: Uint8Array, name: string) {
    const url = URL.createObjectURL(
        new Blob([bytes], { type: 'application/zip' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
